import { supabase } from '../lib/supabase';

/**
 * Service functions for Medical Imaging Requisitions
 * Handles requisition creation, retrieval, and export tracking
 */

// =================================================================
// HELPER FUNCTIONS
// =================================================================

/**
 * Convert snake_case database fields to camelCase
 * @param {Object} data - Database record
 * @returns {Object} Converted object
 */
const convertToCamelCase = (data) => {
  if (!data) return null;

  return {
    id: data?.id,
    requisitionId: data?.requisition_id,
    userId: data?.user_id,
    status: data?.status,
    // Doctor info
    doctorInfo: {
      registrationNumber: data?.doctor_registration_number,
      doctorName: data?.doctor_name,
      specialty: data?.doctor_specialty,
      clinicName: data?.doctor_clinic_name,
      clinicAddress: data?.doctor_clinic_address,
      phoneNumber: data?.doctor_phone,
      email: data?.doctor_email,
      signature: data?.doctor_signature,
      signatureDate: data?.doctor_signature_date
    },
    // Patient info
    patientInfo: {
      patientName: data?.patient_name,
      dateOfBirth: data?.patient_date_of_birth,
      age: data?.patient_age,
      gender: data?.patient_gender,
      idNumber: data?.patient_id_number,
      address: data?.patient_address,
      city: data?.patient_city,
      postalCode: data?.patient_postal_code,
      phoneNumber: data?.patient_phone,
      email: data?.patient_email,
      emergencyContact: data?.patient_emergency_contact,
      emergencyPhone: data?.patient_emergency_phone
    },
    // Clinical info
    clinicalInfo: {
      clinicalHistory: data?.clinical_history,
      symptoms: data?.current_symptoms,
      relevantFindings: data?.relevant_findings,
      previousImaging: data?.previous_imaging,
      medications: data?.current_medications,
      allergies: data?.allergies,
      contraindications: data?.contraindications,
      clinicalQuestion: data?.clinical_question
    },
    // Imaging request
    imagingRequest: {
      modality: data?.imaging_modality,
      bodyRegion: data?.body_region,
      contrast: data?.contrast_required,
      urgency: data?.urgency,
      preferredDate: data?.preferred_date,
      irefer_code: data?.irefer_code,
      additionalNotes: data?.additional_notes
    },
    // Appointment & results
    scheduledDate: data?.scheduled_date,
    facilityName: data?.facility_name,
    facilityAddress: data?.facility_address,
    confirmationNumber: data?.confirmation_number,
    reportUrl: data?.report_url,
    completedDate: data?.completed_date,
    radiologistName: data?.radiologist_name,
    // Export tracking
    exportedAt: data?.exported_at,
    exportFormat: data?.export_format,
    // Timestamps
    createdAt: data?.created_at,
    updatedAt: data?.updated_at
  };
};

// =================================================================
// REQUISITION CRUD OPERATIONS
// =================================================================

/**
 * Save new requisition
 * @param {string} userId - User ID
 * @param {Object} requisitionData - Complete requisition data
 * @returns {Promise<Object>} Saved requisition
 */
export const saveRequisition = async (userId, requisitionData) => {
  try {
    const { data, error } = await supabase?.from('medical_requisitions')?.insert([{
        user_id: userId,
        // Doctor information
        doctor_registration_number: requisitionData?.doctorInfo?.registrationNumber,
        doctor_name: requisitionData?.doctorInfo?.doctorName,
        doctor_specialty: requisitionData?.doctorInfo?.specialty,
        doctor_clinic_name: requisitionData?.doctorInfo?.clinicName,
        doctor_clinic_address: requisitionData?.doctorInfo?.clinicAddress,
        doctor_phone: requisitionData?.doctorInfo?.phoneNumber,
        doctor_email: requisitionData?.doctorInfo?.email,
        doctor_signature: requisitionData?.doctorInfo?.signature,
        doctor_signature_date: new Date()?.toISOString(),
        // Patient information
        patient_name: requisitionData?.patientInfo?.patientName,
        patient_date_of_birth: requisitionData?.patientInfo?.dateOfBirth,
        patient_age: requisitionData?.patientInfo?.age,
        patient_gender: requisitionData?.patientInfo?.gender,
        patient_id_number: requisitionData?.patientInfo?.idNumber,
        patient_address: requisitionData?.patientInfo?.address,
        patient_city: requisitionData?.patientInfo?.city,
        patient_postal_code: requisitionData?.patientInfo?.postalCode,
        patient_phone: requisitionData?.patientInfo?.phoneNumber,
        patient_email: requisitionData?.patientInfo?.email,
        patient_emergency_contact: requisitionData?.patientInfo?.emergencyContact,
        patient_emergency_phone: requisitionData?.patientInfo?.emergencyPhone,
        // Clinical information
        clinical_history: requisitionData?.clinicalInfo?.clinicalHistory,
        current_symptoms: requisitionData?.clinicalInfo?.symptoms,
        relevant_findings: requisitionData?.clinicalInfo?.relevantFindings,
        previous_imaging: requisitionData?.clinicalInfo?.previousImaging,
        current_medications: requisitionData?.clinicalInfo?.medications,
        allergies: requisitionData?.clinicalInfo?.allergies,
        contraindications: requisitionData?.clinicalInfo?.contraindications,
        clinical_question: requisitionData?.clinicalInfo?.clinicalQuestion,
        // Imaging request
        imaging_modality: requisitionData?.imagingRequest?.modality,
        body_region: requisitionData?.imagingRequest?.bodyRegion,
        contrast_required: requisitionData?.imagingRequest?.contrast,
        urgency: requisitionData?.imagingRequest?.urgency,
        preferred_date: requisitionData?.imagingRequest?.preferredDate,
        irefer_code: requisitionData?.imagingRequest?.irefer_code,
        additional_notes: requisitionData?.imagingRequest?.additionalNotes,
        status: 'submitted'
      }])?.select()?.single();

    if (error) throw error;

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error saving requisition:', error);
    throw error;
  }
};

/**
 * Fetch user's requisitions
 * @param {string} userId - User ID
 * @param {number} limit - Number of requisitions to fetch
 * @returns {Promise<Array>} Array of requisitions
 */
export const fetchUserRequisitions = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase?.from('medical_requisitions')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(convertToCamelCase);
  } catch (error) {
    console.error('Error fetching requisitions:', error);
    throw error;
  }
};

/**
 * Fetch single requisition by ID
 * @param {string} requisitionId - Requisition ID
 * @returns {Promise<Object>} Requisition details
 */
export const fetchRequisitionById = async (requisitionId) => {
  try {
    const { data, error } = await supabase?.from('medical_requisitions')?.select('*')?.eq('id', requisitionId)?.single();

    if (error) throw error;

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error fetching requisition:', error);
    throw error;
  }
};

/**
 * Update requisition status
 * @param {string} requisitionId - Requisition ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated requisition
 */
export const updateRequisitionStatus = async (requisitionId, status) => {
  try {
    const { data, error } = await supabase?.from('medical_requisitions')?.update({ status })?.eq('id', requisitionId)?.select()?.single();

    if (error) throw error;

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error updating requisition status:', error);
    throw error;
  }
};

/**
 * Track requisition export
 * @param {string} requisitionId - Requisition ID
 * @param {string} format - Export format (pdf, etc.)
 * @returns {Promise<Object>} Updated requisition
 */
export const trackRequisitionExport = async (requisitionId, format = 'pdf') => {
  try {
    const { data, error } = await supabase?.from('medical_requisitions')?.update({
        exported_at: new Date()?.toISOString(),
        export_format: format
      })?.eq('id', requisitionId)?.select()?.single();

    if (error) throw error;

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error tracking export:', error);
    throw error;
  }
};

/**
 * Delete requisition
 * @param {string} requisitionId - Requisition ID
 * @returns {Promise<boolean>} Success status
 */
export const deleteRequisition = async (requisitionId) => {
  try {
    const { error } = await supabase?.from('medical_requisitions')?.delete()?.eq('id', requisitionId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting requisition:', error);
    throw error;
  }
};